import React, {useEffect, useState} from "react";
import {
    Box,
    Grid,
    Typography,
    Avatar,
    Divider,
    Modal,
    IconButton,
    useTheme,
    useMediaQuery,
    Backdrop,
    Fade,
} from "@mui/material";
import { CreditCard } from "lucide-react"
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import {ApiDetails, KEY} from "../../../../Constants";
import Chip from "@mui/material/Chip";
import CryptoJS from "crypto-js";
import imageAvatar from "../../../../assets/Tenant_avatar.jpg";
import { styled } from "@mui/system";
import LeaseManagementPanel from "../LeaseManagement/LeaseManagementPanel";
import { handle401Unauthorized, handle403Forbidden } from '../../../../utils/authErrorHandler';
import { getUserData } from '../../../../services/api/client';


// Styled components for the modal
const StyledModal = styled(Modal)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1),
    }
}));

const ImageContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    maxWidth: '90vw',
    maxHeight: '90vh',
    backgroundColor: 'white',
    borderRadius: theme.spacing(1),
    // boxShadow: theme.shadows[24],
    outline: 'none',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
        maxWidth: '95vw',
        maxHeight: '95vh',
        borderRadius: theme.spacing(0.5),
    }
}));

const PreviewImage = styled('img')(({ theme }) => ({
    width: '100%',
    height: 'auto',
    maxWidth: '600px',
    maxHeight: '600px',
    objectFit: 'contain',
    display: 'block',
    [theme.breakpoints.down('sm')]: {
        maxWidth: '100%',
        maxHeight: '70vh',
    }
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    color: 'white',
    zIndex: 1,
    '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
    },
    [theme.breakpoints.down('sm')]: {
        top: theme.spacing(0.5),
        right: theme.spacing(0.5),
    }
}));

const ClickableAvatar = styled(Avatar)(({ theme }) => ({
    cursor: 'pointer',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    position: 'relative',
    '&:hover': {
        transform: 'scale(1.05)',
        // boxShadow: theme.shadows[8],
    },
    '&:hover::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }
}));

const ZoomIconOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    opacity: 0,
    transition: 'opacity 0.2s ease-in-out',
    pointerEvents: 'none',
    color: 'white',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: '50%',
    padding: theme.spacing(1),
    '.clickable-avatar:hover &': {
        opacity: 1,
    }
}));

const ResponsiveContainer = styled(Box)(({ theme }) => ({
    margin: "20px",
    marginTop: "20px",
    padding: "20px",
    borderRadius: "8px",
    border: "1px solid #E0E0E0",
    backgroundColor: "#F9FAFB",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.05)",
    [theme.breakpoints.down('md')]: {
        margin: "12px",
        marginTop: "12px",
        padding: "16px",
    },
    [theme.breakpoints.down('sm')]: {
        margin: "8px",
        marginTop: "8px",
        padding: "12px",
        borderRadius: "6px",
    }
}));

const LeftSide = (props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Image modal state
    const [imageModalOpen, setImageModalOpen] = useState(false);

    const [tenantData, setTenantData] = useState({
        first_name: "n/a",
        last_name: "n/a",
        phone_number: "",
        secondary_phone_number: "",
        email_address: "",
        property_name: "",
        account_number: "",
        house_number: "",
        Wallet: 0,
        status: "",
        state: ""
    });

    const [originalText, setOriginalText] = useState(() => {
        const encryptedData = localStorage.getItem("myMap");
        return encryptedData
            ? CryptoJS.AES.decrypt(encryptedData, KEY).toString(CryptoJS.enc.Utf8)
            : "";
    });

    // Helper function to get auth token
    const getAuthToken = () => {
        const userData = getUserData();
        return userData ? userData.find(([key]) => key === 'access_token')?.[1] : null;
    };

    const [imageSrc, setImageSrc] = useState("");

    useEffect(() => {
        // Fetch the tenant data
        fetch(ApiDetails + "pegasus/visionary/tenant/get/specific/tenant", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": getAuthToken() || ""
            },
            body: JSON.stringify({tenant_id: props.tenantID, property_id: props.propertyID}),
        } )

            .then((response) => response.json())
            .then((data) => {
                setTenantData(data);
            })
            .catch((err) => console.error(err));
    }, []);

    useEffect(() => {
        // Fetch the image from the server
        fetch(
            ApiDetails + "pegasus/visionary/tenant/fetch/tenant/files",
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": getAuthToken() || ""
                },
                body: JSON.stringify({
                    tenant_id: props.tenantID,
                }),
            }
        )
            .then(async (response) => {
                let data = await response.json();
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    let pic = data.tenant_photo;
                    if (
                        pic.base64 !== "No File Found" &&
                        pic.base64.toString().trim() !== ""
                    ) {
                        setImageSrc("data:image/jpeg;base64," + pic.base64);
                    }
                }
            })
            .catch((err) => {
                console.log(err);
            });
    }, []);

    // Handle avatar click
    const handleAvatarClick = () => {
        setImageModalOpen(true);
    };

    // Handle modal close
    const handleModalClose = () => {
        setImageModalOpen(false);
    };

    // Get the image source for display
    const displayImageSrc = imageSrc || imageAvatar;
    const hasCustomImage = imageSrc !== "";

    return (
        <>
            <ResponsiveContainer>
                {/* Customer Header */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        marginBottom: isMobile ? "16px" : "20px",
                    }}
                >
                    <Box
                        className="clickable-avatar"
                        sx={{
                            position: 'relative',
                            marginBottom: "10px"
                        }}
                    >
                        <ClickableAvatar
                            sx={{
                                width: isMobile ? 70 : 80,
                                height: isMobile ? 70 : 80,
                                backgroundColor: "#E0E0E0",
                            }}
                            src={displayImageSrc}
                            onClick={handleAvatarClick}
                        />
                        {hasCustomImage && (
                            <ZoomIconOverlay>
                                <ZoomInIcon fontSize="small" />
                            </ZoomIconOverlay>
                        )}
                    </Box>

                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: "bold",
                            color: "#2C5283",
                            textAlign: "center",
                            fontSize: isMobile ? '1.1rem' : '1.25rem'
                        }}
                    >
                        {tenantData.first_name} {tenantData.last_name}
                    </Typography>
                    {tenantData.email_address && (
                        <Box sx={{display: "flex", alignItems: "center", gap: "5px", textAlign: "center"}}>
                            <EmailIcon fontSize="small" sx={{color: "#2C5283"}}/>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{
                                    wordBreak: "break-word",
                                    fontSize: isMobile ? '0.8rem' : '0.875rem'
                                }}
                            >
                                {tenantData.email_address}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{display: "flex", alignItems: "center", gap: "5px"}}>
                        <PhoneIcon fontSize="small" sx={{color: "#2C5283"}}/>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                        >
                            {tenantData.phone_number}
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{marginY: isMobile ? "8px" : "10px", backgroundColor: "#2C5283"}}/>

                {/* Details Section */}
                <Box sx={{marginBottom: isMobile ? "16px" : "20px"}}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: "bold",
                            marginBottom: isMobile ? "8px" : "10px",
                            fontSize: isMobile ? '1rem' : '1.1rem'
                        }}
                    >
                        Identification Details
                    </Typography>
                    <Grid container spacing={isMobile ? 0.5 : 1}>
                        <Grid item xs={6}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#4A5568",
                                    fontSize: isMobile ? '0.8rem' : '0.875rem'
                                }}
                            >
                                National ID:
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sx={{textAlign: "right"}}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: "bold",
                                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                                }}
                            >
                                {tenantData.national_id}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{marginY: isMobile ? "8px" : "10px", backgroundColor: "#2C5283"}}/>

                {/* Details Section */}
                <Box sx={{marginBottom: isMobile ? "16px" : "20px"}}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: "bold",
                            marginBottom: isMobile ? "8px" : "10px",
                            fontSize: isMobile ? '1rem' : '1.1rem'
                        }}
                    >
                        Property Details
                    </Typography>
                    <Grid container spacing={isMobile ? 0.5 : 1}>
                        <Grid item xs={6}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#4A5568",
                                    fontSize: isMobile ? '0.8rem' : '0.875rem'
                                }}
                            >
                                Names:
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sx={{textAlign: "right"}}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: "bold",
                                    wordBreak: "break-word",
                                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                                }}
                            >
                                {tenantData.property_name}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#4A5568",
                                    fontSize: isMobile ? '0.8rem' : '0.875rem'
                                }}
                            >
                                House Number:
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sx={{textAlign: "right"}}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: "bold",
                                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                                }}
                            >
                                {tenantData.house_number}
                            </Typography>
                        </Grid>

                        <Grid item xs={6}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#4A5568",
                                    fontSize: isMobile ? '0.8rem' : '0.875rem'
                                }}
                            >
                                Account Number:
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sx={{textAlign: "right"}}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: "bold",
                                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                                }}
                            >
                                {tenantData.account_number}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{marginY: isMobile ? "8px" : "10px", backgroundColor: "#2C5283"}}/>

                <Box sx={{marginBottom: isMobile ? "16px" : "20px"}}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: "bold",
                            marginBottom: isMobile ? "8px" : "10px",
                            fontSize: isMobile ? '1rem' : '1.1rem'
                        }}
                    >
                        Contact Information
                    </Typography>
                    <Grid container spacing={isMobile ? 0.5 : 1}>
                        {tenantData.email_address && (
                            <>
                                <Grid item xs={6}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "#4A5568",
                                            fontSize: isMobile ? '0.8rem' : '0.875rem'
                                        }}
                                    >
                                        Email:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} sx={{textAlign: "right"}}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: "bold",
                                            wordBreak: "break-word",
                                            fontSize: isMobile ? '0.7rem' : '0.875rem'
                                        }}
                                    >
                                        {tenantData.email_address}
                                    </Typography>
                                </Grid>
                            </>
                        )}

                        {tenantData.phone_number && (
                            <>
                                <Grid item xs={6}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "#4A5568",
                                            fontSize: isMobile ? '0.8rem' : '0.875rem'
                                        }}
                                    >
                                        Phone Number:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} sx={{textAlign: "right"}}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: "bold",
                                            fontSize: isMobile ? '0.75rem' : '0.875rem'
                                        }}
                                    >
                                        {tenantData.phone_number}
                                    </Typography>
                                </Grid>
                            </>
                        )}

                        {tenantData.secondary_phone_number && (
                            <>
                                <Grid item xs={6}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "#4A5568",
                                            fontSize: isMobile ? '0.8rem' : '0.875rem'
                                        }}
                                    >
                                        Secondary Phone:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} sx={{textAlign: "right"}}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: "bold",
                                            fontSize: isMobile ? '0.75rem' : '0.875rem'
                                        }}
                                    >
                                        {tenantData.secondary_phone_number}
                                    </Typography>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Box>

                <Divider sx={{backgroundColor: "#2C5283"}}/>

                <Box sx={{marginBottom: isMobile ? "16px" : "20px"}}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: "bold",
                            marginBottom: isMobile ? "8px" : "10px",
                            fontSize: isMobile ? '1rem' : '1.1rem'
                        }}
                    >
                        Account Details
                    </Typography>
                    <Grid container spacing={isMobile ? 0.5 : 1}>
                        <Grid item xs={6}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#4A5568",
                                    fontSize: isMobile ? '0.8rem' : '0.875rem'
                                }}
                            >
                                Wallet Balance:
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sx={{textAlign: "right"}}>
                            <Typography variant="body2" sx={{
                                fontWeight: "bold",
                                color: tenantData.status === "COMPLETED" ? "green" : "red",
                                fontSize: isMobile ? '0.75rem' : '0.875rem'
                            }}>
                                {parseInt(
                                    tenantData.Wallet.toString().replace(",", "")
                                ).toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "KSH",
                                })}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#4A5568",
                                    fontSize: isMobile ? '0.8rem' : '0.875rem'
                                }}
                            >
                                Payment Status:
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sx={{textAlign: "right"}}>
                            <Chip
                                label={tenantData.status}
                                color={tenantData.status === "COMPLETED" ? "success" : "error"}
                                variant="outlined"
                                size={isMobile ? "small" : "medium"}
                                sx={{
                                    fontWeight: "bold",
                                    fontSize: isMobile ? '0.7rem' : '0.75rem'
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{backgroundColor: "#2C5283"}}/>

                <Box sx={{marginBottom: isMobile ? "16px" : "20px"}}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: "bold",
                            marginBottom: isMobile ? "8px" : "10px",
                            fontSize: isMobile ? '1rem' : '1.1rem'
                        }}
                    >
                        Status Details
                    </Typography>
                    <Grid container spacing={isMobile ? 0.5 : 1}>
                        <Grid item xs={6}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#4A5568",
                                    fontSize: isMobile ? '0.8rem' : '0.875rem'
                                }}
                            >
                                Account Status:
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sx={{textAlign: "right"}}>
                            <Chip
                                label={tenantData.state}
                                color={tenantData.state === "ACTIVE" ? "success" : "error"}
                                variant="outlined"
                                size={isMobile ? "small" : "medium"}
                                sx={{
                                    fontWeight: "bold",
                                    fontSize: isMobile ? '0.7rem' : '0.75rem'
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#4A5568",
                                    fontSize: isMobile ? '0.8rem' : '0.875rem'
                                }}
                            >
                                Date Created:
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sx={{textAlign: "right"}}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: "bold",
                                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                                }}
                            >
                                {tenantData.date_created}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* Lease Management Panel */}
                <LeaseManagementPanel
                    lease={tenantData}
                    landlordId={JSON.parse(originalText)[0][1]}
                    onLeaseUpdated={() => {
                        // Reload the page to reflect updates
                        window.location.reload();
                    }}
                />
            </ResponsiveContainer>

            {/* Image Preview Modal */}
            <StyledModal
                open={imageModalOpen}
                onClose={handleModalClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 300,
                    sx: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
                }}
            >
                <Fade in={imageModalOpen}>
                    <ImageContainer>
                        <CloseButton
                            onClick={handleModalClose}
                            size={isMobile ? "small" : "medium"}
                            aria-label="Close image preview"
                        >
                            <CloseIcon />
                        </CloseButton>

                        <Box sx={{ p: 2 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    mb: 2,
                                    textAlign: 'center',
                                    color: '#2C5283',
                                    fontWeight: 'bold',
                                    fontSize: isMobile ? '1.1rem' : '1.25rem'
                                }}
                            >
                                {tenantData.first_name} {tenantData.last_name}
                            </Typography>

                            <PreviewImage
                                src={displayImageSrc}
                                alt={`${tenantData.first_name} ${tenantData.last_name}`}
                                onError={(e) => {
                                    e.target.src = imageAvatar;
                                }}
                            />

                            {!hasCustomImage && (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{
                                        textAlign: 'center',
                                        mt: 2,
                                        fontStyle: 'italic'
                                    }}
                                >
                                    Default avatar - No custom photo uploaded
                                </Typography>
                            )}
                        </Box>
                    </ImageContainer>
                </Fade>
            </StyledModal>
        </>
    );
};

export default LeftSide;